import React, { useState, useEffect } from "react";
import { communityServicesAPI } from "../../../services/api";

function VolunteersReport() {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10); // Fixed to 10 rows
  const [searchTerm, setSearchTerm] = useState("");

  const [volunteersData, setVolunteersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVolunteersReport = async () => {
    try {
      setLoading(true);
      const response = await communityServicesAPI.getVolunteerReports();

      if (response.data && response.data.success) {
        setVolunteersData(response.data.data.volunteer_reports || []);
      } else {
        setError("Invalid response structure from server");
        setVolunteersData([]);
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to fetch volunteers report. Please try again later.");
      setVolunteersData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVolunteersReport();

    const interval = setInterval(fetchVolunteersReport, 300000);

    return () => clearInterval(interval);
  }, []);

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const filteredData = volunteersData.filter(
    (volunteer) =>
      volunteer.volunteer_name &&
      volunteer.volunteer_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleExport = () => {
    if (!filteredData.length) {
      alert("No volunteer data to export.");
      return;
    }

    const headers = [
      "Volunteer Name",
      "Programs Participated",
      "Hours Contributed",
    ];
    const rows = filteredData.map((volunteer) => [
      `"${volunteer.volunteer_name}"`,
      volunteer.no_programs_participated,
      volunteer.hours_contributed_mock_data,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Volunteers_Report_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {/* Volunteers Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: "#DBEAFE" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
              style={{ color: "#2563EB" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Volunteers Who Participated
            </h3>
            <p className="text-sm text-gray-600">
              <strong>{volunteersData.length}</strong> volunteers participated
              in total
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors hover:bg-blue-700"
            style={{ backgroundColor: "#2563EB" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search volunteers..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-5 absolute left-3 top-2.5 text-gray-400"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
            />
          </svg>
        </div>
      </div>

      {/* Table Section */}
      <div className="border border-gray-300 rounded-lg bg-white">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Volunteer Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  No. of Programs Participated In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours Contributed
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan="3"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Loading volunteers data...
                  </td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((volunteer) => (
                  <tr
                    key={volunteer.volunteer_id}
                    className="hover:bg-gray-50"
                  >
                    {/* Volunteer Name */}
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-xs"
                          title={volunteer.volunteer_name}
                        >
                          {getInitials(volunteer.volunteer_name)}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {volunteer.volunteer_name}
                        </span>
                      </div>
                    </td>

                    {/* Programs Participated  */}
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {volunteer.no_programs_participated}
                      </div>
                    </td>

                    {/* Hours Contributed  */}
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {volunteer.hours_contributed_mock_data} hours
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {error
                      ? error
                      : volunteersData.length === 0
                        ? "No volunteers data available."
                        : "No volunteers found matching your search."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* custom pagination na walang row number selection */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-300">
            <div className="flex items-center">
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {totalItems === 0 ? 0 : startIndex + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(endIndex, totalItems)}
                </span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`p-1.5 rounded border ${currentPage === 1
                  ? "text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                title="First Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15L5.25 12l7.5 7.5" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`p-1.5 rounded border ${currentPage === 1
                  ? "text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                title="Previous Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>

              <span className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded border ${currentPage === totalPages
                  ? "text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                title="Next Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded border ${currentPage === totalPages
                  ? "text-gray-400 border-gray-300 cursor-not-allowed"
                  : "text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                title="Last Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default VolunteersReport;